import { memo, FC, useMemo, useState, useEffect } from 'react'
import { Drawer, Form, Input, Tabs, Radio, Space, theme } from 'antd'
import styles from './index.module.scss'
import { ReactComponent as CloseModalIcon } from '@/assets/icons/closeModal.svg'
import { ReactComponent as ArrowLeftIcon } from '@/assets/icons/arrowLeft.svg'
import { ReactComponent as FastIcon } from '@/assets/icons/fast.svg'
import { ReactComponent as NormalIcon } from '@/assets/icons/normal.svg'
import { ReactComponent as UltrafastIcon } from '@/assets/icons/ultrafast.svg'
import { useTranslation } from '@/services/i18n'
import { FeeLevelTypeEnum } from '@/constants/enum'
import classNames from 'classnames'
import { FeeLevelModel, FeeRateDto } from '@/services/apis/tx/models'
import { toFixed } from '@/utils/number'
import { useCoinBasis } from '@/hooks/coin'
import DrawerFooter from '@/components/DrawerFooter'
import fee from '@/utils/fee'
import CustomFeePanel from '../CustomFeePanel'
import { LOOP_FEE_INTERVAL } from '@/constants'
import { useCalcUsdValue } from '@/hooks/tx'
import { CoinImplTag } from '@/services/apis/coin/models'

interface FeeLoopCircleProps {
  percent: number
}

const FeeLoopCircle: FC<FeeLoopCircleProps> = ({ percent }) => {
  const token: any = theme.useToken()

  const R = 5

  const circleLength = R * 2 * Math.PI
  const percentOffset = circleLength - circleLength * percent

  return (
    <svg width={14} height={14}>
      <circle
        fill="none"
        cx={7}
        cy={7}
        r={R}
        transform="rotate(-90, 7, 7)"
        origin={'7,7'}
        strokeWidth={2}
        stroke={token.token.colorPrimary}
        strokeDasharray={circleLength}
        strokeDashoffset={percentOffset}
      />
    </svg>
  )
}

interface LevelPanelProps {
  support1559: boolean
  feeLevel?: FeeLevelModel
  selectedLevel: FeeLevelTypeEnum
  setSelectedLevel: (value: FeeLevelTypeEnum) => void
  countDown: number
  feeDecimal: number
  coinImplTag?: CoinImplTag
}

const LevelPanel: FC<LevelPanelProps> = ({
  feeDecimal,
  support1559,
  feeLevel,
  selectedLevel,
  setSelectedLevel,
  countDown,
  coinImplTag,
}) => {
  const { t } = useTranslation()
  const levels = [
    {
      icon: <UltrafastIcon />,
      title: t('fee.ultrafast'),
      value: FeeLevelTypeEnum.HIGH,
      data: feeLevel?.highFeeRate,
    },
    {
      icon: <FastIcon />,
      title: t('fee.fast'),
      value: FeeLevelTypeEnum.MIDDLE,
      data: feeLevel?.middleFeeRate,
    },
    {
      icon: <NormalIcon />,
      title: t('fee.normal'),
      value: FeeLevelTypeEnum.LOW,
      data: feeLevel?.lowFeeRate,
    },
  ]
  const cls = classNames([styles.inputLike, 'flex-1'])
  return (
    <>
      <Radio.Group
        value={selectedLevel}
        className={styles.levelGroup}
        onChange={evt => {
          const { value } = evt.target
          setSelectedLevel(value)
        }}
      >
        <Space direction="vertical" className={styles.space}>
          {levels.map(({ icon, title, value, data }) => {
            let gasPrice
            switch (coinImplTag) {
              case 'aptos':
                gasPrice = data?.gasUnitPrice
                break
              default:
                gasPrice = data?.feeRate || data?.fee
                break
            }
            return (
              <Radio className={styles.levelItem} value={value} key={value}>
                <div className={cls}>
                  <div className={styles.feeLevelName}>
                    {icon}
                    <span className={styles.levelItemTitle}>{title}</span>
                  </div>
                  <div className={styles.feeRateContainer}>
                    <div className={styles.feeRate}>
                      {toFixed(gasPrice, feeDecimal)} {feeLevel?.feeUnit}
                    </div>
                    {support1559 && (
                      <div className={styles.feeRate1559}>
                        <div>Priority {toFixed(data?.maxPriorityFee, 2)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </Radio>
            )
          })}
        </Space>
      </Radio.Group>
      <div className={styles.refresh}>
        <FeeLoopCircle percent={(LOOP_FEE_INTERVAL - countDown) / LOOP_FEE_INTERVAL} />
        <span className={styles.refreshText}>{t('fee.refreshLater', { time: countDown })}</span>
      </div>
    </>
  )
}

export interface GasSettingData {
  feeRate?: FeeRateDto
  feeLevel?: FeeLevelTypeEnum
  feeType?: 'level' | 'custom'
}

interface GasSettingProps {
  open: boolean
  onClose: () => void
  onOk: (data: GasSettingData) => void
  coinKey: string
  feeLevelModel?: FeeLevelModel
  estimateFee: (realFeeRateDto?: FeeRateDto) => [string, string]
  isSpeeding?: boolean
  countDown: number
}

const GasSetting: FC<GasSettingProps> = ({
  open,
  onClose,
  coinKey,
  onOk,
  feeLevelModel,
  estimateFee,
  isSpeeding,
  countDown,
}) => {
  const { t } = useTranslation()
  const [activeTabKey, setActiveTabKey] = useState<'level' | 'custom'>('level')
  const [selectedLevel, setSelectedLevel] = useState<FeeLevelTypeEnum>(FeeLevelTypeEnum.MIDDLE)
  const [customError, setCustomError] = useState(false)
  const [customFeeRate, setCustomFeeRate] = useState<FeeRateDto>({})
  const coinBasis = useCoinBasis(coinKey)
  const feeCoinBasis = useCoinBasis(coinBasis?.feeCoinKey)
  const isSupportEIP1559 = coinBasis?.feeType === 1
  const isSpeed = typeof isSpeeding === 'boolean'
  const feeDecimal = coinBasis?.coinImplTag === 'fil' ? 5 : 2
  const items = useMemo(() => {
    return [
      {
        key: 'level',
        label: t('fee.selectGas'),
        children: (
          <LevelPanel
            feeDecimal={feeDecimal}
            support1559={isSupportEIP1559}
            feeLevel={feeLevelModel}
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
            countDown={countDown}
            coinImplTag={coinBasis?.coinImplTag}
          />
        ),
      },
      {
        key: 'custom',
        label: t('fee.custom'),
        children: (
          <CustomFeePanel
            isSpeed={isSpeed}
            customFeeRate={customFeeRate}
            setCustomError={setCustomError}
            setCustomFeeRate={setCustomFeeRate}
            coinImplTag={coinBasis?.coinImplTag}
            isSupportEIP1559={isSupportEIP1559}
            coinKey={coinKey}
            feeLevelModel={feeLevelModel!}
          />
        ),
      },
    ]
  }, [selectedLevel, setSelectedLevel, isSupportEIP1559, feeLevelModel, customFeeRate, countDown])

  // 预估网络费
  const estimatedFee = useMemo(() => {
    const feeRate =
      activeTabKey === 'custom' ? customFeeRate : fee.getLevelFeeRate(selectedLevel, feeLevelModel)
    return estimateFee(feeRate)
  }, [customFeeRate, selectedLevel, activeTabKey, feeLevelModel])

  // level面板更改后影响自定义面板
  useEffect(() => {
    if (activeTabKey === 'level') {
      const selectedFeeRate = fee.getLevelFeeRate(selectedLevel, feeLevelModel)
      setCustomFeeRate({
        feeRate: selectedFeeRate?.feeRate,
        maxFee: selectedFeeRate?.maxFee,
        baseFee: selectedFeeRate?.baseFee,
        maxPriorityFee: selectedFeeRate?.maxPriorityFee,
        gasLimit: selectedFeeRate?.gasLimit,
        // sui
        gasBudget: selectedFeeRate?.gasBudget,
        // aptos
        maxGasAmount: selectedFeeRate?.maxGasAmount,
        gasUnitPrice: selectedFeeRate?.gasUnitPrice,
      })
    }
  }, [selectedLevel, activeTabKey, feeLevelModel])

  const feeUsdValue = useCalcUsdValue(estimatedFee[0], feeCoinBasis?.exchangeRateUsd)

  const titlekey = isSpeed ? 'wallet.sendDrawer.txSpeed' : 'wallet.sendDrawer.gasSetting'
  const cls = classNames({
    [styles.drawer]: true,
    [styles.speedDrawer]: isSpeed,
  })
  return (
    <Drawer
      title={t(titlekey)}
      placement="right"
      open={open}
      width={560}
      closeIcon={<ArrowLeftIcon onClick={onClose} />}
      className={cls}
      maskClosable={false}
      destroyOnClose={true}
      extra={
        <div onClick={onClose} className={styles.closeIcon}>
          <CloseModalIcon />
        </div>
      }
      footer={
        <DrawerFooter
          okText={isSpeed ? t('tx.speed') : t('common.save')}
          onOk={() => {
            onOk({
              feeLevel: selectedLevel,
              feeType: activeTabKey,
              feeRate: customFeeRate,
            })
          }}
          isLoading={isSpeeding}
          disabledOk={(activeTabKey === 'custom' && customError) || !feeLevelModel}
          onCancel={onClose}
        />
      }
    >
      {isSpeed && <div className={styles.alertMsg}>{t('tx.speedAlert')}</div>}
      <Form layout="vertical">
        <Form.Item label={t('fee.estimatedFee')}>
          <Input value={`${estimatedFee[0]} ${estimatedFee[1]} ($ ${feeUsdValue})`} disabled />
        </Form.Item>
        <Tabs
          activeKey={activeTabKey}
          items={items}
          onChange={val => setActiveTabKey(val as 'level' | 'custom')}
        />
      </Form>
    </Drawer>
  )
}

export default memo(GasSetting)
